import { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const RACE_FIELDS = [
  ["venue", "Venue", "text"], ["state", "State", "text"], ["date", "Date", "text"], ["start_time", "Local Time (e.g. 1:50PM)", "text"], ["race_number", "Race #", "number"],
  ["race_name", "Race Name", "text"], ["distance", "Distance (m)", "number"], ["race_class", "Class", "text"],
  ["track_condition", "Track", "text"], ["prize", "Prize", "text"],
];

const HORSE_FIELDS = [
  ["number", "#", "number", "w-12"], ["name", "Name", "text", "w-40"], ["barrier", "Bar", "number", "w-14"],
  ["weight", "Kg", "number", "w-16"], ["jockey", "Jockey", "text", "w-32"], ["trainer", "Trainer", "text", "w-32"],
  ["form", "Form", "text", "w-20"], ["career_starts", "Sts", "number", "w-14"], ["career_wins", "W", "number", "w-12"],
  ["career_places", "Pl", "number", "w-12"], ["jockey_win_pct", "J%", "number", "w-14"], ["trainer_win_pct", "T%", "number", "w-14"],
  ["speed_rating", "Speed", "number", "w-16"], ["days_since_run", "Days", "number", "w-14"], ["market_price", "Mkt $", "number", "w-16"],
];

const cell = "bg-[#0F172A] border border-slate-800 rounded px-2 py-1 text-xs num focus:outline-none focus:border-emerald-500 transition-colors";
const toVal = (type, v) => (type === "number" ? (v === "" ? null : Number(v)) : v || null);

export const RawDataEditor = ({ race, onSaved }) => {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(race)));
  const [busy, setBusy] = useState(false);

  const setRace = (k, t) => (e) => setDraft({ ...draft, [k]: toVal(t, e.target.value) });
  const setHorse = (i, k, t) => (e) => {
    const horses = [...draft.horses];
    horses[i] = { ...horses[i], [k]: t ? toVal(t, e.target.value) : e.target.checked };
    setDraft({ ...draft, horses });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/races/${race.id}`, draft);
      toast.success("Raw data saved, ratings recalculated");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="raw-data-editor">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {RACE_FIELDS.map(([k, label, t]) => (
          <label key={k} className="block">
            <span className="eyebrow">{label}</span>
            <input data-testid={`raw-race-${k}`} type={t} value={draft[k] ?? ""} onChange={setRace(k, t)} className={`${cell} w-full mt-1 py-1.5 text-sm`} />
          </label>
        ))}
      </div>
      <div className="scroll-x">
        <table className="text-xs">
          <thead>
            <tr className="eyebrow text-left">
              {HORSE_FIELDS.map(([k, label]) => <th key={k} className="px-1 py-2 font-normal">{label}</th>)}
              <th className="px-1 py-2 font-normal">Scr</th>
              <th className="px-1 py-2 font-normal">Em</th>
            </tr>
          </thead>
          <tbody>
            {draft.horses.map((h, i) => (
              <tr key={i} className={h.scratched || h.emergency ? "opacity-40" : ""}>
                {HORSE_FIELDS.map(([k, , t, w]) => (
                  <td key={k} className="px-1 py-1">
                    <input data-testid={`raw-horse-${i}-${k}`} type={t} step="any" value={h[k] ?? ""} onChange={setHorse(i, k, t)} className={`${cell} ${w}`} />
                  </td>
                ))}
                <td className="px-1 py-1 text-center">
                  <input data-testid={`raw-horse-${i}-scratched`} type="checkbox" checked={!!h.scratched} onChange={setHorse(i, "scratched")} className="accent-rose-500" />
                </td>
                <td className="px-1 py-1 text-center">
                  <input data-testid={`raw-horse-${i}-emergency`} type="checkbox" checked={!!h.emergency} onChange={setHorse(i, "emergency")} className="accent-amber-500" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={save} disabled={busy} data-testid="raw-data-save-button" className="font-semibold">
        <Save className="h-4 w-4 mr-2" />{busy ? "Saving..." : "Save & Recalculate"}
      </Button>
    </div>
  );
};
